import { useCallback, useEffect } from 'react';

import {
  Card,
  Center,
  Collapse,
  ConditionalWrapper,
  Flex,
  Group,
  ImageMantine,
  Stack,
  Swipeable,
  Text,
  ThemeIcon,
  Transition,
  cx,
} from 'ui';
import { UseFormReturnType, isNotEmpty, useForm } from 'ui/Form';
import { IconDelete } from 'ui/Icons';

import { ICredential } from '../../../api';
import { TokenExCVV, useBreakPoints, useFormListValidator } from '../../../hooks';
import classes from './ExistingCreditCard.module.css';

export interface ExistingCreditCardProps {
  credential: ICredential;
  onClick?: () => void;
  isActive?: boolean;
  isError?: boolean;
  variant?: 'default' | 'secondary';
}

export type ExistingCredentialsForm = {
  credentialId?: ICredential['uuid'];
  isCvvValid?: boolean;
};

export type ExistingCredentialForm = {
  isCvvValid?: boolean;
  isValidEntry: boolean;
};
export const ExistingCreditCard = ({
  credential,
  isActive = false,
  isError = false,
  variant = 'default',
  onClick,
}: ExistingCreditCardProps) => {
  return (
    <Card
      radius="sm"
      className={cx(classes.ExistingCreditCard__cardBase, {
        [classes['ExistingCreditCard--activeCard']]: isActive === true,
        [classes['ExistingCreditCard--erroredCard']]: isError === true,
        [classes['ExistingCreditCard--secondaryCard']]: variant === 'secondary',
      })}
      bd="1px solid gray.1"
      onClick={onClick}
    >
      <Group justify="space-between" mih="40px" wrap="nowrap">
        <Group wrap="nowrap">
          <ImageMantine fit="contain" w={62} h="auto" src={credential.image} />
          <Flex>
            <Text fz={14} mr="xs">{`•••• ••••`}</Text>
            <Text fz={14}>{`•••• ${credential.last_four}`}</Text>
          </Flex>
        </Group>
        <Text fz={14} c={'gray.4'}>
          {`${credential.month}/${credential.year.slice(2, 4)}`}
        </Text>
      </Group>
    </Card>
  );
};
type SwipeAbleCardProps = {
  onAction: () => void;
} & ExistingCreditCardProps;

const SwipeableCard = ({ onAction, onClick, credential, ...props }: SwipeAbleCardProps) => {
  const { isSmallScreen } = useBreakPoints();
  return (
    <ConditionalWrapper
      condition={!!isSmallScreen}
      wrapper={(children) => (
        <Swipeable
          rightAction={(props) => (
            <Swipeable.Action {...props} color="cerise" onSwipe={onAction}>
              <ThemeIcon variant="transparent" color="white">
                <IconDelete />
              </ThemeIcon>
            </Swipeable.Action>
          )}
        >
          {children}
        </Swipeable>
      )}
    >
      <ExistingCreditCard onClick={onClick} credential={credential} {...props} />
    </ConditionalWrapper>
  );
};

ExistingCreditCard.Swipeable = SwipeableCard;

const CredentialCardInput = ({
  credential,
  form,
}: {
  credential: ICredential;
  form: UseFormReturnType<ExistingCredentialForm>;
}) => {
  const noop = () => {};

  useEffect(() => {
    form.validate();
  }, [form.values.isCvvValid]);

  useFormListValidator({ form, id: 'existing_credential' });

  return (
    <TokenExCVV
      cardType={credential.card_type}
      config={credential.iframe}
      onChange={noop}
      setIsCvvValid={(error) => form.setFieldValue('isValidEntry', error)}
    />
  );
};

export const CredentialCardForm = ({
  credential,
  isActive = false,
  onClick,
  isGroupError = false,
  setIsCvvValid,
}: {
  credential: ICredential;
  isActive?: boolean;
  onClick: () => void;
  isGroupError?: boolean;
  setIsCvvValid: (status: boolean) => void;
}) => {
  const form = useForm<ExistingCredentialForm>({
    initialValues: {
      isCvvValid: undefined,
      isValidEntry: false, // Determines if the cvv has actually been entered correctly, not if the cvv is valid for the card.
    },
    validate: {
      isCvvValid: (value) => (value === false ? 'Please enter a valid CVV' : undefined),
      isValidEntry: (value) => (value == true ? undefined : 'Please enter a valid CVV'),
    },
    validateInputOnBlur: ['isValidEntry'],
  });

  const cardErrorStatus = !!Object.keys(form.getInputProps('isCvvValid')?.error ?? {})?.length;
  const cardFormError = isActive && cardErrorStatus;

  // Reset the cvv valid field if the isValidEntry changes
  useEffect(() => {
    form.setFieldValue('isCvvValid', undefined);
  }, [form.values.isValidEntry]);

  useEffect(() => {
    if (!isActive) {
      form.reset();
    }
  }, [isActive]);

  useEffect(() => {
    setIsCvvValid(form.isValid());
  }, [form.values]);

  return (
    <Stack gap={'xs'}>
      <Flex align={'center'} gap={'sm'} onClick={onClick}>
        <ExistingCreditCard
          credential={credential}
          isActive={isActive}
          isError={isGroupError || cardFormError}
        />
        <Transition mounted={isActive} transition="fade" timingFunction="ease">
          {(style) => (
            <Center style={style} maw={{ base: '100px', md: '107px' }} miw="95px">
              {isActive && <CredentialCardInput credential={credential} form={form} />}
            </Center>
          )}
        </Transition>
      </Flex>

      <Collapse in={isActive && !form.isValid()}>
        <Text fz={12} fw={600} c={form.errors.isCvvValid ? 'cerise.7' : 'cobalt.6'}>
          {form.errors.isCvvValid ? form.errors.isCvvValid : ' Enter CVV to verify your card'}
        </Text>
      </Collapse>
    </Stack>
  );
};

export const ExistingCardGroup = ({
  credentials,
  onCredentialChange,
}: {
  credentials: ICredential[];
  onCredentialChange: (credentialId?: ICredential['uuid']) => void;
}) => {
  const form = useForm<ExistingCredentialsForm>({
    initialValues: {
      credentialId: undefined,
      isCvvValid: false,
    },
    validate: {
      credentialId: isNotEmpty(),
      isCvvValid: (value) => (value == true ? undefined : null),
    },
  });

  const handleCredentialChange = useCallback(
    (credential: ICredential) => {
      form.setFieldValue('credentialId', credential.uuid);
      form.setFieldValue('isCvvValid', false);
    },
    [form]
  );

  const noCredentialError = form.errors.credentialId;

  useEffect(() => {
    onCredentialChange(form.values.credentialId);
  }, [form.values, onCredentialChange]);

  useFormListValidator({ form, id: 'existing_credentials' });
  return (
    <Stack gap={'xs'}>
      <Text type="body" fw={600}>
        Select a Card on File
      </Text>
      {credentials?.map((credential: ICredential) => (
        <CredentialCardForm
          key={credential.uuid}
          credential={credential}
          onClick={() => handleCredentialChange(credential)}
          isActive={form.values.credentialId == credential.uuid}
          setIsCvvValid={(isValid) => form.setFieldValue('isCvvValid', isValid)}
          isGroupError={Boolean(noCredentialError)}
        />
      ))}
      {noCredentialError && (
        <Text fz={12} c={'cerise.7'}>
          Please select a payment method
        </Text>
      )}
    </Stack>
  );
};
